import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Archive, CalendarClock, ChevronDown, Clock3, LockKeyhole, MessageSquareText, Pencil, Save, Send, ShieldCheck, UserRound, X } from "lucide-react";
import {
  isAgentProposalMessageDraftWithinBound,
  type AgentProposal,
  type BoardSnapshot,
  type CreateWorkItem,
  type UpdateWorkItem,
  type WorkAgentState,
  type WorkItem,
  type WorkKind,
  type WorkPriority,
  type WorkStatus
} from "@business-ops/domain";
import { api, ApiError, type WorkNote } from "../api.js";
import { ACTIVE_STATUSES, STATUS_LABELS, clientName, formatDeadline, fromLocalDateTime, memberLabel, memberName, toLocalDateTime } from "../helpers.js";
import { ProposalCard, proposalAttentionKind, type AgentWorkQueue } from "../views/review.js";
import { Drawer, Field } from "./ui.js";
import { SafeLinkedText } from "./safe-linked-text.js";

export function AddWorkDrawer({ open, initialDueDay = null, board, close, create, notify }: {
  open: boolean;
  initialDueDay?: string | null;
  board: BoardSnapshot;
  close: () => void;
  create: (input: CreateWorkItem) => Promise<WorkItem>;
  notify: (text: string, tone?: "success" | "error") => void;
}) {
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [taskStartAt, setTaskStartAt] = useState("");
  const [taskEndAt, setTaskEndAt] = useState("");
  const [workKind, setWorkKind] = useState<WorkKind>("TASK");
  const [scheduledStartAt, setScheduledStartAt] = useState("");
  const [scheduledEndAt, setScheduledEndAt] = useState("");
  const [scheduledAllDay, setScheduledAllDay] = useState(false);
  const [description, setDescription] = useState("");
  const availableProjects = useMemo(() => board.projects.filter((project) => !clientId || project.clientId === clientId), [board.projects, clientId]);

  useEffect(() => {
    if (!open) return;
    // WHY [BOL-CALENDAR-MANUAL-ADD-001]: Calendar Add means a real scheduled
    // engagement, while Board Add means ordinary task Work. Both create the
    // same canonical Work record; a deadline remains a separate optional fact.
    // Falsifier: Calendar Add silently creates an end-of-day deadline, or the
    // same record cannot be edited from Board and Calendar.
    const initial = initialWorkTimingForCalendarDay(initialDueDay);
    setWorkKind(initial.workKind);
    setScheduledStartAt(initial.scheduledStartAt);
    setScheduledEndAt(initial.scheduledEndAt);
    setScheduledAllDay(false);
    setDueAt("");
    setTaskStartAt("");
    setTaskEndAt("");
  }, [initialDueDay, open]);

  const submit = async () => {
    if (!title.trim()) return notify("Give the work a clear title.", "error");
    const rangeError = workKind === "TASK" ? taskRangeInputError(taskStartAt, taskEndAt) : null;
    if (rangeError) return notify(rangeError, "error");
    setSaving(true);
    try {
      if (workKind === "ENGAGEMENT" && !scheduledStartAt) return notify("Choose when this is scheduled.", "error");
      await create({
        title: title.trim(), description: description.trim(), clientId: clientId || null,
        projectId: projectId || null, assigneeId: assigneeId || null,
        assignedByMemberId: board.viewer.id,
        assignedAt: new Date().toISOString(),
        department: null,
        filesReference: null,
        reviewFeedbackReference: null,
        dueAt: dueAt ? fromLocalDateTime(dueAt, board.workspace.timezone) : null, status: "BACKLOG", priority: "NORMAL",
        workKind,
        taskStartAt: workKind === "TASK" && taskStartAt ? fromLocalDateTime(`${taskStartAt}T00:00`, board.workspace.timezone) : null,
        taskEndAt: workKind === "TASK" && taskEndAt ? fromLocalDateTime(`${taskEndAt}T00:00`, board.workspace.timezone) : null,
        scheduledStartAt: workKind === "ENGAGEMENT" ? fromLocalDateTime(scheduledAllDay ? `${scheduledStartAt.slice(0, 10)}T00:00` : scheduledStartAt, board.workspace.timezone) : null,
        scheduledEndAt: workKind === "ENGAGEMENT" && !scheduledAllDay && scheduledEndAt ? fromLocalDateTime(scheduledEndAt, board.workspace.timezone) : null,
        scheduledAllDay: workKind === "ENGAGEMENT" && scheduledAllDay
      });
      notify("Work added.");
      setTitle(""); setClientId(""); setProjectId(""); setAssigneeId(""); setDueAt(""); setTaskStartAt(""); setTaskEndAt(""); setDescription("");
      setWorkKind("TASK"); setScheduledStartAt(""); setScheduledEndAt(""); setScheduledAllDay(false);
      close();
    } catch (error) { notify(error instanceof Error ? error.message : "That work did not save.", "error"); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} close={close} eyebrow="New item" title="Add work" footer={<><button type="button" className="secondary-button" onClick={close}>Cancel</button><button type="button" className="primary-button" disabled={saving} onClick={() => void submit()}>{saving ? "Adding…" : "Add work"}</button></>}>
      <div className="form-stack">
        <Field label="What needs to be done?"><input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Clear work title" maxLength={240} /></Field>
        <div className="form-grid"><Field label="Type"><select value={workKind} onChange={(event) => { const next = event.target.value as WorkKind; setWorkKind(next); if (next === "TASK") { setScheduledStartAt(""); setScheduledEndAt(""); setScheduledAllDay(false); } else { setTaskStartAt(""); setTaskEndAt(""); } }}><option value="TASK">Task</option><option value="ENGAGEMENT">Meeting, shoot, or event</option></select></Field><Field label="Owner"><select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}><option value="">Unassigned</option>{board.members.map((member) => <option key={member.id} value={member.id}>{memberLabel(board.members, member)}</option>)}</select></Field></div>
        {workKind === "TASK" ? <div className="form-stack form-stack--compact"><div className="form-grid"><Field label="Timeline start" hint="Optional. Choose both dates."><input type="date" value={taskStartAt} onChange={(event) => setTaskStartAt(event.target.value)} /></Field><Field label="Timeline end"><input type="date" value={taskEndAt} onChange={(event) => setTaskEndAt(event.target.value)} /></Field></div></div> : null}
        {workKind === "ENGAGEMENT" ? <div className="form-stack form-stack--compact">
          <label className="lock-control"><input type="checkbox" checked={scheduledAllDay} onChange={(event) => setScheduledAllDay(event.target.checked)} /><span><CalendarClock size={18} /><strong>All day</strong><small>Hide the clock time on Calendar.</small></span></label>
          <div className="form-grid"><Field label={scheduledAllDay ? "Date" : "Starts"}><input type={scheduledAllDay ? "date" : "datetime-local"} value={scheduledAllDay ? scheduledStartAt.slice(0, 10) : scheduledStartAt} onChange={(event) => setScheduledStartAt(scheduledAllDay ? `${event.target.value}T00:00` : event.target.value)} /></Field>{scheduledAllDay ? null : <Field label="Ends (optional)"><input type="datetime-local" value={scheduledEndAt} onChange={(event) => setScheduledEndAt(event.target.value)} /></Field>}</div>
        </div> : null}
        <details className="drawer-disclosure">
          <summary><span>Client and notes</span><ChevronDown size={18} /></summary>
          <div className="drawer-disclosure__body">
            <div className="form-grid"><Field label="Client"><select value={clientId} onChange={(event) => { setClientId(event.target.value); setProjectId(""); }}><option value="">No client yet</option>{board.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></Field><Field label="Project"><select value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">No project yet</option>{availableProjects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></Field></div>
            <Field label="Deadline" hint="Optional. This is separate from the scheduled event time."><input type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} /></Field>
            <Field label="Notes" hint="Optional context, links, or expected output."><textarea rows={4} value={description} onChange={(event) => setDescription(event.target.value)} /></Field>
          </div>
        </details>
      </div>
    </Drawer>
  );
}

export function initialWorkTimingForCalendarDay(day: string | null | undefined): {
  workKind: WorkKind;
  scheduledStartAt: string;
  scheduledEndAt: string;
} {
  const calendarDay = day && /^\d{4}-\d{2}-\d{2}$/u.test(day) ? day : null;
  return calendarDay
    ? { workKind: "ENGAGEMENT", scheduledStartAt: `${calendarDay}T09:00`, scheduledEndAt: `${calendarDay}T10:00` }
    : { workKind: "TASK", scheduledStartAt: "", scheduledEndAt: "" };
}

export function WorkDetailDrawer({ selected, board, canArchive, close, update, notify, agentQueue }: {
  selected: WorkItem | null;
  board: BoardSnapshot;
  canArchive: boolean;
  close: () => void;
  update: (item: WorkItem, input: Omit<UpdateWorkItem, "expectedVersion">) => Promise<WorkItem>;
  notify: (text: string, tone?: "success" | "error") => void;
  agentQueue: AgentWorkQueue;
}) {
  const current = selected ? board.workItems.find((item) => item.id === selected.id) ?? selected : null;
  const [draft, setDraft] = useState<WorkDraft | null>(() => current ? makeDraft(current, board.workspace.timezone) : null);
  const [manualPanel, setManualPanel] = useState<"NONE" | "CONTEXT" | "EDIT">("NONE");
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<WorkNote[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(current ? makeDraft(current, board.workspace.timezone) : null); setNote(""); setManualPanel("NONE"); }, [board.workspace.timezone, current?.id, current?.version]);
  useEffect(() => {
    if (!current) { setNotes(null); return; }
    let active = true;
    setNotes(null);
    void api.notes(current.id).then((response) => { if (active) setNotes(response.notes); }).catch(() => { if (active) setNotes([]); });
    return () => { active = false; };
  }, [current?.id]);
  if (!current || !draft) return <Drawer open={false} close={close} title="Work"><span /></Drawer>;

  const save = async () => {
    if (!draft.title.trim()) return notify("The title cannot be empty.", "error");
    const rangeError = draft.workKind === "TASK" ? taskRangeInputError(draft.taskStartAt, draft.taskEndAt) : null;
    if (rangeError) return notify(rangeError, "error");
    setSaving(true);
    try {
      await update(current, {
        title: draft.title.trim(), description: draft.description.trim(), status: draft.status, priority: draft.priority,
        clientId: draft.clientId || null, projectId: draft.projectId || null, assigneeId: draft.assigneeId || null,
        filesReference: draft.filesReference.trim() || null,
        reviewFeedbackReference: draft.reviewFeedbackReference.trim() || null,
        dueAt: draft.dueAt ? fromLocalDateTime(draft.dueAt, board.workspace.timezone) : null,
        workKind: draft.workKind,
        taskStartAt: draft.workKind === "TASK" && draft.taskStartAt ? fromLocalDateTime(`${draft.taskStartAt}T00:00`, board.workspace.timezone) : null,
        taskEndAt: draft.workKind === "TASK" && draft.taskEndAt ? fromLocalDateTime(`${draft.taskEndAt}T00:00`, board.workspace.timezone) : null,
        scheduledStartAt: draft.workKind === "ENGAGEMENT" && draft.scheduledStartAt
          ? fromLocalDateTime(draft.scheduledAllDay ? `${draft.scheduledStartAt.slice(0, 10)}T00:00` : draft.scheduledStartAt, board.workspace.timezone)
          : null,
        scheduledEndAt: draft.workKind === "ENGAGEMENT" && !draft.scheduledAllDay && draft.scheduledEndAt
          ? fromLocalDateTime(draft.scheduledEndAt, board.workspace.timezone)
          : null,
        scheduledAllDay: draft.workKind === "ENGAGEMENT" && draft.scheduledAllDay,
        blockedReason: draft.blockedReason.trim() || null, manualLock: draft.manualLock
      });
      // WHY [BOL-AGENT-FIRST-WORK-002]: editing task fields and adding missing
      // context are separate, explicit corrections. Never smuggle an unfinished
      // context draft into an unrelated task-field save. Falsifier: Save changes
      // creates a WorkNote without a separate Save context decision.
      notify("Saved.");
      close();
    } catch (error) {
      if (error instanceof ApiError && error.code === "VERSION_CONFLICT") notify("Someone updated this while you had it open. Review the current version before saving again.", "error");
      else notify(error instanceof Error ? error.message : "That change did not save. Try again.", "error");
    } finally { setSaving(false); }
  };

  const saveContext = async () => {
    if (!note.trim()) return notify("Add the missing context first.", "error");
    setSaving(true);
    try {
      await api.addNote(current.id, note.trim());
      const response = await api.notes(current.id);
      setNotes(response.notes);
      setNote("");
      await agentQueue.afterBoardChange();
      setManualPanel("NONE");
      notify("Context added.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "That context did not save. Try again.", "error");
    } finally { setSaving(false); }
  };

  const set = <K extends keyof WorkDraft>(key: K, value: WorkDraft[K]) => setDraft((old) => old ? { ...old, [key]: value } : old);
  const setWorkKind = (workKind: WorkKind) => setDraft((old) => old ? {
    ...old,
    workKind,
    ...(workKind === "TASK"
      ? { scheduledStartAt: "", scheduledEndAt: "", scheduledAllDay: false }
      : { taskStartAt: "", taskEndAt: "" })
  } : old);
  const projects = board.projects.filter((project) => !draft.clientId || project.clientId === draft.clientId);
  const visibleStatus = draft.status === "ARCHIVED" ? current.status : draft.status;
  const proposals = agentQueue.proposals.filter((proposal) => proposal.workItemId === current.id);
  const primaryProposal = proposals[0] ?? null;
  const agentState = (board.workAgentStates ?? []).find((state) => state.workItemId === current.id) ?? null;
  const showAgentState = shouldShowAgentStateCard(agentState, proposals.length);
  const drawerFooter = manualPanel === "CONTEXT" ? <><button type="button" className="secondary-button" disabled={saving} onClick={() => setManualPanel("NONE")}>Cancel</button><button type="button" className="primary-button" disabled={saving || !note.trim()} onClick={() => void saveContext()}><MessageSquareText size={17} />{saving ? "Saving…" : "Save context"}</button></> : manualPanel === "EDIT" ? <><button type="button" className="secondary-button" disabled={saving} onClick={() => { setDraft(makeDraft(current, board.workspace.timezone)); setManualPanel("NONE"); }}>Cancel</button><button type="button" className="primary-button" disabled={saving} onClick={() => void save()}><Save size={17} />{saving ? "Saving…" : "Save changes"}</button></> : undefined;

  return (
    <Drawer open close={close} eyebrow={clientName(board, current)} title={current.title} footer={drawerFooter}>
      <div className="form-stack">
        {primaryProposal ? <section className="drawer-agent-action" aria-label={`${proposals.length} agent ${proposals.length === 1 ? "recommendation" : "recommendations"}`}><header><span><i />{proposalDrawerLabel(primaryProposal)}</span>{proposals.length > 1 ? <small>1 of {proposals.length}</small> : null}</header><ProposalCard key={primaryProposal.id} proposal={primaryProposal} board={board} queue={agentQueue} embedded /></section> : null}
        {showAgentState && agentState ? <AgentStateCard state={agentState} board={board} queue={agentQueue} /> : null}
        <section className="task-at-a-glance" aria-label="Current work"><span><i className={`status-dot status-dot--${current.status.toLowerCase()}`} /><small>Status</small><strong>{STATUS_LABELS[current.status]}</strong></span><span><UserRound size={16} /><small>Owner</small><strong>{memberName(board, current.assigneeId)}</strong></span>{current.workKind === "ENGAGEMENT" ? <span><CalendarClock size={16} /><small>Scheduled</small><strong>{formatDeadline(current.scheduledStartAt ?? null)}</strong></span> : current.taskStartAt && current.taskEndAt ? <span><CalendarClock size={16} /><small>Timeline</small><strong>{formatTaskRange(current.taskStartAt, current.taskEndAt, board.workspace.timezone)}</strong></span> : null}<span><CalendarClock size={16} /><small>Deadline</small><strong>{formatDeadline(current.dueAt)}</strong></span></section>
        {current.description ? <p className="task-current-description"><SafeLinkedText text={current.description} /></p> : null}
        {current.filesReference || current.reviewFeedbackReference ? <section className="form-stack form-stack--compact" aria-label="Task references">
          {current.filesReference ? <p className="task-current-description"><strong>Files: </strong><SafeLinkedText text={current.filesReference} /></p> : null}
          {current.reviewFeedbackReference ? <p className="task-current-description"><strong>Review: </strong><SafeLinkedText text={current.reviewFeedbackReference} /></p> : null}
        </section> : null}
        {!proposals.length && !agentState ? <p className="agent-watching-state">Nothing needs approval. The agent is still watching this task.</p> : null}

        <div className="task-secondary-actions" aria-label="Manual task options">
          <button type="button" aria-expanded={manualPanel === "CONTEXT"} className={manualPanel === "CONTEXT" ? "is-active" : ""} onClick={() => setManualPanel((open) => open === "CONTEXT" ? "NONE" : "CONTEXT")}><MessageSquareText size={17} /><span><strong>Add missing context</strong><small>For details shared outside WhatsApp</small></span></button>
          <button type="button" aria-expanded={manualPanel === "EDIT"} className={manualPanel === "EDIT" ? "is-active" : ""} onClick={() => setManualPanel((open) => open === "EDIT" ? "NONE" : "EDIT")}><Pencil size={17} /><span><strong>Edit task</strong><small>Correct status, owner, deadline, or details</small></span></button>
        </div>

        {manualPanel === "CONTEXT" ? <section className="task-manual-panel"><div className="note-box note-box--primary"><div><MessageSquareText size={18} /><strong>What did the agent miss?</strong></div><textarea autoFocus rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add the missing details." /></div></section> : null}

        {manualPanel === "EDIT" ? <section className="task-manual-panel" aria-label="Edit task">
          <div className="form-grid"><Field label="Status"><select value={visibleStatus} onChange={(event) => set("status", event.target.value as WorkStatus)}>{ACTIVE_STATUSES.map((status) => <option value={status} key={status}>{STATUS_LABELS[status]}</option>)}</select></Field><Field label="Owner"><select value={draft.assigneeId} onChange={(event) => set("assigneeId", event.target.value)}><option value="">Unassigned</option>{board.members.map((member) => <option key={member.id} value={member.id}>{memberLabel(board.members, member)}</option>)}</select></Field></div>
          <div className="form-grid"><Field label="Type"><select value={draft.workKind} onChange={(event) => setWorkKind(event.target.value as WorkKind)}><option value="TASK">Task</option><option value="ENGAGEMENT">Meeting, shoot, or event</option></select></Field><Field label="Deadline"><input type="datetime-local" value={draft.dueAt} onChange={(event) => set("dueAt", event.target.value)} /></Field></div>
          {draft.workKind === "TASK" ? <div className="form-grid"><Field label="Timeline start" hint="Optional. Choose both dates."><input type="date" value={draft.taskStartAt} onChange={(event) => set("taskStartAt", event.target.value)} /></Field><Field label="Timeline end"><input type="date" value={draft.taskEndAt} onChange={(event) => set("taskEndAt", event.target.value)} /></Field></div> : null}
          {draft.workKind === "ENGAGEMENT" ? <div className="form-stack form-stack--compact"><label className="lock-control"><input type="checkbox" checked={draft.scheduledAllDay} onChange={(event) => set("scheduledAllDay", event.target.checked)} /><span><CalendarClock size={18} /><strong>All day</strong><small>Hide the clock time on Calendar.</small></span></label><div className="form-grid"><Field label={draft.scheduledAllDay ? "Date" : "Starts"}><input type={draft.scheduledAllDay ? "date" : "datetime-local"} value={draft.scheduledAllDay ? draft.scheduledStartAt.slice(0, 10) : draft.scheduledStartAt} onChange={(event) => set("scheduledStartAt", draft.scheduledAllDay ? `${event.target.value}T00:00` : event.target.value)} /></Field>{draft.scheduledAllDay ? null : <Field label="Ends (optional)"><input type="datetime-local" value={draft.scheduledEndAt} onChange={(event) => set("scheduledEndAt", event.target.value)} /></Field>}</div></div> : null}
          {visibleStatus === "BLOCKED" ? <Field label="What is blocking it?"><textarea rows={3} value={draft.blockedReason} onChange={(event) => set("blockedReason", event.target.value)} placeholder="What is stopping this, and who can unblock it?" /></Field> : null}
          <details className="drawer-disclosure">
            <summary><span>More details</span><ChevronDown size={18} /></summary>
            <div className="drawer-disclosure__body">
              <Field label="Title"><input value={draft.title} onChange={(event) => set("title", event.target.value)} /></Field>
              <Field label="Priority"><select value={draft.priority} onChange={(event) => set("priority", event.target.value as WorkPriority)}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></Field>
              <div className="form-grid"><Field label="Client"><select value={draft.clientId} onChange={(event) => { set("clientId", event.target.value); set("projectId", ""); }}><option value="">No client</option>{board.clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></Field><Field label="Project"><select value={draft.projectId} onChange={(event) => set("projectId", event.target.value)}><option value="">No project</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></Field></div>
              <Field label="Description"><textarea rows={4} value={draft.description} onChange={(event) => set("description", event.target.value)} /></Field>
              <Field label="Files location / attachments"><input maxLength={4_000} value={draft.filesReference} placeholder="HTTP(S) link or plain location" onChange={(event) => set("filesReference", event.target.value)} /></Field>
              <Field label="Review / feedback location"><input maxLength={4_000} value={draft.reviewFeedbackReference} placeholder="HTTP(S) link or plain location" onChange={(event) => set("reviewFeedbackReference", event.target.value)} /></Field>
              <label className="lock-control"><input type="checkbox" checked={draft.manualLock} onChange={(event) => set("manualLock", event.target.checked)} /><span><LockKeyhole size={18} /><strong>Human control</strong><small>Pause automatic changes to this item. It can still appear as a risk.</small></span></label>
              {/* WHY: archiving removes the item from active views. It remains a
                  deliberate advanced choice and is recoverable through Activity. */}
              {canArchive ? <label className="archive-control"><input type="checkbox" checked={draft.status === "ARCHIVED"} onChange={(event) => set("status", event.target.checked ? "ARCHIVED" : current.status)} /><span><Archive size={18} /><strong>Archive this item</strong><small>Hide it from active work. Activity can undo this change.</small></span></label> : null}
            </div>
          </details>
        </section> : null}

        <details className="drawer-disclosure notes-disclosure">
          <summary><span>Previous updates <small>{notes?.length ?? 0}</small></span><ChevronDown size={18} /></summary>
          <div className="drawer-disclosure__body"><section className="notes-history" aria-label="Previous updates">{notes === null ? <p>Loading updates.</p> : notes.length === 0 ? <p>No previous updates.</p> : <div>{notes.map((item) => <article key={item.id}><p><SafeLinkedText text={item.body} /></p><small>{item.createdByName} · {new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(item.createdAt))}</small></article>)}</div>}</section></div>
        </details>
      </div>
    </Drawer>
  );
}

// WHY [BOL-SCHEDULE-CONTROLS-WITH-PROPOSAL-001]: a recommendation and an
// already-approved future send are independent obligations on the same Work.
// Queue length may deduplicate ordinary status cards, but it must never hide the
// only Edit/Cancel control for a live schedule. Falsifier: adding another
// recommendation makes an approved schedule impossible to review or cancel.
export function shouldShowAgentStateCard(state: WorkAgentState | null, proposalCount: number): boolean {
  if (!state) return false;
  if (proposalCount === 0) return true;
  return state.stage === "SCHEDULED" || state.stage === "SCHEDULE_NEEDS_REVIEW";
}

// WHY [BOL-WAITING-VISIBILITY-001]: the deadline describes unfinished work;
// the agent state describes who needs to act next. Show both facts together so
// a sent follow-up can remain honestly overdue without looking unsent or stuck.
// Only safe member/group labels reach this card; transport identities stay in
// the connector database and a delivery-uncertain state never offers Retry.
function AgentStateCard({ state, board, queue }: { state: WorkAgentState; board: BoardSnapshot; queue: AgentWorkQueue }) {
  const [scheduledProposal, setScheduledProposal] = useState<AgentProposal | null>(null);
  const [scheduleLocalDateTime, setScheduleLocalDateTime] = useState("");
  const [scheduleMessage, setScheduleMessage] = useState(state.message);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleControlsFailed, setScheduleControlsFailed] = useState(false);
  useEffect(() => {
    setScheduleMessage(state.message);
    setEditingSchedule(false);
    setScheduleControlsFailed(false);
    setScheduledProposal(null);
    if (state.stage !== "SCHEDULED") return;
    let active = true;
    void api.proposal(state.proposalId).then((proposal) => {
      if (!active || proposal.effect.kind !== "SEND_MESSAGE" || !proposal.effect.schedule) return;
      setScheduledProposal(proposal);
      setScheduleLocalDateTime(proposal.effect.schedule.localDateTime);
      setScheduleMessage(proposal.messageDraft ?? state.message);
    }).catch(() => { if (active) setScheduleControlsFailed(true); });
    return () => { active = false; };
  }, [state.message, state.proposalId, state.stage, state.updatedAt]);

  const saveSchedule = async () => {
    if (!scheduledProposal || scheduledProposal.effect.kind !== "SEND_MESSAGE" || !scheduledProposal.effect.schedule) return;
    setScheduleBusy(true);
    try {
      await api.editScheduledProposal(scheduledProposal.id, {
        expectedVersion: scheduledProposal.version,
        revision: scheduledProposal.currentRevision,
        localDateTime: scheduleLocalDateTime,
        messageDraft: scheduleMessage
      });
      // WHY [BOL-SCHEDULE-WORK-EDIT-001]: editing an approved future send
      // revokes the old job and creates a fresh Manual-review revision. Reload
      // both canonical projections so the drawer cannot keep showing the old
      // time as authorized. Falsifier: Save leaves the old schedule active or
      // bypasses a second approval.
      await queue.afterBoardChange();
      setEditingSchedule(false);
      queue.notify("Schedule changed. Review and approve it again.");
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That schedule did not change.", "error");
    } finally {
      setScheduleBusy(false);
    }
  };

  const cancelSchedule = async () => {
    if (!scheduledProposal || !window.confirm("Cancel this scheduled message? Nothing will be sent by this schedule.")) return;
    setScheduleBusy(true);
    try {
      await api.cancelScheduledProposal(scheduledProposal.id, scheduledProposal.version, scheduledProposal.currentRevision);
      // WHY [BOL-SCHEDULE-WORK-CANCEL-001]: the database races cancellation
      // against the durable execution job. Refresh before claiming success;
      // no browser flag may imply a won cancellation while transport owns it.
      await queue.afterBoardChange();
      queue.notify("Schedule canceled. Nothing was sent.");
    } catch (error) {
      queue.notify(error instanceof Error ? error.message : "That schedule was not canceled.", "error");
    } finally {
      setScheduleBusy(false);
    }
  };

  const stage = state.stage === "FINAL_CHECKS"
    ? { label: "Final safety check", detail: "Approved. The agent is rechecking the exact person, group, and current conversation before one send.", icon: ShieldCheck }
    : state.stage === "WAITING_FOR_REPLY"
      ? { label: "Waiting for reply", detail: "The follow-up was sent once. This task stays overdue until the work is actually finished.", icon: Clock3 }
      : state.stage === "MESSAGE_SENT"
        ? { label: "Message sent", detail: "Sent once. Delivery and read status change only when WhatsApp reports them.", icon: Send }
        : state.stage === "SCHEDULED"
          ? { label: "Scheduled message", detail: `Approved for ${formatAgentScheduleTime(state)}. It has not been sent.`, icon: CalendarClock }
          : state.stage === "SCHEDULE_NEEDS_REVIEW"
            ? { label: "Schedule stopped", detail: `${scheduleFailureLabel(state)} Nothing was retried.`, icon: AlertTriangle }
            : { label: "Delivery needs checking", detail: "WhatsApp did not confirm the result. Check the chat; the agent will not retry or send again.", icon: AlertTriangle };
  const StageIcon = stage.icon;
  const target = state.targetMemberId ? memberName(board, state.targetMemberId) : state.proposalType === "ANSWER_MESSAGE" ? "Whole chat" : "No named person";
  return (
    <section className={`drawer-agent-state drawer-agent-state--${state.stage.toLowerCase()}`} aria-label={stage.label}>
      <header><span><StageIcon size={18} aria-hidden="true" /></span><div><strong>{stage.label}</strong><p>{stage.detail}</p></div></header>
      <dl className="agent-state-route">
        <div><dt>Person</dt><dd>{target}</dd></div>
        <div><dt>Group</dt><dd>{state.destinationLabel}</dd></div>
      </dl>
      <div className="agent-delivery-line">
        <span className={`agent-delivery-badge agent-delivery-badge--${(state.deliveryState ?? "PENDING").toLowerCase()}`}>{deliveryStateLabel(state)}</span>
        {state.scheduledAt ? <small><CalendarClock size={14} aria-hidden="true" />{formatAgentScheduleTime(state)}</small> : null}
        {state.sentAt ? <small><Send size={14} aria-hidden="true" />{formatAgentTime(state.sentAt)}</small> : null}
        {state.nextCheckAt ? <small><Clock3 size={14} aria-hidden="true" />Next check {formatAgentTime(state.nextCheckAt)}</small> : null}
      </div>
      <details className="agent-sent-message"><summary>Message <ChevronDown size={16} aria-hidden="true" /></summary><p>{state.message}</p></details>
      {state.stage === "SCHEDULED" ? <section className="agent-schedule-controls" aria-label="Scheduled message controls">
        {scheduledProposal ? <>
          <div className="agent-schedule-controls__actions">
            <button className="secondary-button" type="button" disabled={scheduleBusy} aria-expanded={editingSchedule} onClick={() => setEditingSchedule((open) => !open)}><Pencil size={15} />Edit schedule</button>
            <button className="quiet-review-action" type="button" disabled={scheduleBusy} onClick={() => void cancelSchedule()}><X size={15} />Cancel schedule</button>
          </div>
          {editingSchedule ? <div className="agent-schedule-editor">
            <Field label="Send at"><input type="datetime-local" step={1} value={scheduleLocalDateTime} disabled={scheduleBusy} onChange={(event) => setScheduleLocalDateTime(event.target.value)} /></Field>
            <Field label="Message"><textarea rows={3} maxLength={600} value={scheduleMessage} disabled={scheduleBusy} onChange={(event) => {
              if (isAgentProposalMessageDraftWithinBound("FOLLOWUP_MESSAGE", event.target.value)) {
                setScheduleMessage(event.target.value);
              }
            }} /></Field>
            <p>Saving revokes the old time. The new schedule must be approved again.</p>
            <div><button className="secondary-button" type="button" disabled={scheduleBusy} onClick={() => setEditingSchedule(false)}>Cancel edit</button><button className="primary-button" type="button" disabled={scheduleBusy || !scheduleLocalDateTime || !scheduleMessage.trim()} onClick={() => void saveSchedule()}><Save size={15} />{scheduleBusy ? "Saving…" : "Save for review"}</button></div>
          </div> : null}
        </> : <p>{scheduleControlsFailed ? "Schedule controls are unavailable. Refresh and try again." : "Loading schedule controls…"}</p>}
      </section> : null}
    </section>
  );
}

function deliveryStateLabel(state: WorkAgentState): string {
  if (state.deliveryState === "PLAYED") return "Played";
  if (state.deliveryState === "READ") return "Read";
  if (state.deliveryState === "DELIVERED") return "Delivered";
  if (state.deliveryState === "SENT") return "Sent";
  if (state.stage === "SCHEDULED") return "Not sent yet";
  if (state.stage === "SCHEDULE_NEEDS_REVIEW") return "Stopped before send";
  return state.stage === "FINAL_CHECKS" ? "Not sent yet" : "No receipt yet";
}

export function formatAgentScheduleTime(state: WorkAgentState): string {
  if (!state.scheduledAt || !state.scheduleTimeZone || !state.scheduleUtcOffset) return "the approved time";
  const label = new Intl.DateTimeFormat(undefined, {
    timeZone: state.scheduleTimeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(state.scheduledAt));
  return `${label} · ${state.scheduleTimeZone} · UTC${state.scheduleUtcOffset}`;
}

export function scheduleFailureLabel(state: WorkAgentState): string {
  const labels: Readonly<Record<NonNullable<WorkAgentState["scheduleFailureReason"]>, string>> = {
    EXPIRED: "The approved send window expired.",
    WORK_CHANGED: "The task changed after approval.",
    CONVERSATION_CHANGED: "The conversation changed after approval.",
    ROUTE_CHANGED: "The WhatsApp destination changed after approval.",
    POLICY_CHANGED: "The messaging rule changed after approval.",
    TARGET_CHANGED: "The responsible person changed after approval.",
    TIMEZONE_CHANGED: "The approved time no longer matches the workspace timezone.",
    TRANSPORT_CHANGED: "WhatsApp sending was unavailable at the approved time.",
    OTHER: "A final safety check stopped the schedule."
  };
  return state.scheduleFailureReason ? labels[state.scheduleFailureReason] : labels.OTHER;
}

function formatAgentTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function proposalDrawerLabel(proposal: AgentProposal): string {
  const attention = proposalAttentionKind(proposal);
  if (attention === "IDENTITY_DECISION") return "Identity to confirm";
  if (attention === "WAITING") return "Agent is waiting";
  return proposal.effect.kind === "SEND_MESSAGE" ? "Draft ready" : "Update ready";
}

interface WorkDraft {
  title: string;
  description: string;
  filesReference: string;
  reviewFeedbackReference: string;
  status: WorkStatus;
  priority: WorkPriority;
  clientId: string;
  projectId: string;
  assigneeId: string;
  dueAt: string;
  workKind: WorkKind;
  taskStartAt: string;
  taskEndAt: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  scheduledAllDay: boolean;
  blockedReason: string;
  manualLock: boolean;
}

function makeDraft(item: WorkItem, timezone: string): WorkDraft {
  return { title: item.title, description: item.description, filesReference: item.filesReference ?? "", reviewFeedbackReference: item.reviewFeedbackReference ?? "", status: item.status, priority: item.priority, clientId: item.clientId ?? "", projectId: item.projectId ?? "", assigneeId: item.assigneeId ?? "", dueAt: toLocalDateTime(item.dueAt, timezone), workKind: item.workKind ?? "TASK", taskStartAt: toLocalDateTime(item.taskStartAt ?? null, timezone).slice(0, 10), taskEndAt: toLocalDateTime(item.taskEndAt ?? null, timezone).slice(0, 10), scheduledStartAt: toLocalDateTime(item.scheduledStartAt ?? null, timezone), scheduledEndAt: toLocalDateTime(item.scheduledEndAt ?? null, timezone), scheduledAllDay: item.scheduledAllDay ?? false, blockedReason: item.blockedReason ?? "", manualLock: item.manualLock };
}

// WHY [BOL-TIMELINE-PAIR-001]: a one-sided or reversed range cannot render a
// truthful task bar. Reject it before mutation instead of guessing a missing
// endpoint. Falsifier: UI submits one endpoint or an end before its start.
export function taskRangeInputError(startDay: string, endDay: string): string | null {
  if (!startDay && !endDay) return null;
  if (!startDay || !endDay) return "Choose both Timeline start and Timeline end.";
  return endDay < startDay ? "Timeline end cannot be before Timeline start." : null;
}

function formatTaskRange(startAt: string, endAt: string, timeZone: string): string {
  const format = new Intl.DateTimeFormat(undefined, { timeZone, month: "short", day: "numeric" });
  return `${format.format(new Date(startAt))} – ${format.format(new Date(endAt))}`;
}
